import React, { useState, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import DataTable, { useServerTable } from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import Input, { Select } from '../components/ui/Input';
import { adminApi } from '../config/axios';
import { useGlobal } from '../contexts/GlobalContext';
import { dateFormat, exportToCSV, useDocumentTitle } from '../config/utils';

const ACTION_TYPES = [
  '', 'login', 'logout', 'create', 'update', 'delete', 'export', 'view',
];

export default function AuditLogs() {
  useDocumentTitle('Audit Logs');
  const { showToast } = useGlobal();
  const [userFilter, setUserFilter]   = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [detail, setDetail]           = useState(null);

  const fetcher = useCallback(async ({ page, perPage }) => {
    const res = await adminApi.post(`/datatable/logs?page=${page + 1}&per_page=${perPage}`, {
      order_by: { created_at: 'desc' },
      filters: {
        ...(userFilter   ? { user_name:  { value: userFilter,   matchMode: 'contains' } } : {}),
        ...(actionFilter ? { action:     { value: actionFilter, matchMode: 'contains' } } : {}),
      },
      filters_date: {
        ...(dateFrom || dateTo ? {
          created_at: { from: dateFrom || '', to: dateTo || '' },
        } : {}),
      },
    });
    return res.data;
  }, [userFilter, actionFilter, dateFrom, dateTo]);

  const table = useServerTable(fetcher);

  const handleExport = () => {
    exportToCSV('audit-logs', table.data, [
      { key: 'user_name',  label: 'User'   },
      { key: 'action',     label: 'Action' },
      { key: 'ip',         label: 'IP'     },
      { key: 'created_at', label: 'Date'   },
    ]);
    showToast('success', 'Exported', 'Audit log downloaded.');
  };

  const columns = [
    { key: 'user_name', header: 'User', render: r => (
      <div>
        <p className="text-text-main text-sm">{r.user_name ?? '—'}</p>
        {r.user_email && <p className="text-text-muted text-xs">{r.user_email}</p>}
      </div>
    )},
    { key: 'action', header: 'Action', render: r => (
      <code className="text-xs bg-white/[0.06] text-accent px-2 py-0.5 rounded font-mono">{r.action}</code>
    )},
    { key: 'description', header: 'Description', render: r => (
      <span className="text-sm text-text-muted truncate max-w-xs block">{r.description ?? '—'}</span>
    )},
    { key: 'ip',         header: 'IP', render: r => <code className="text-xs text-text-muted font-mono">{r.ip ?? '—'}</code> },
    { key: 'created_at', header: 'When', render: r => dateFormat(r.created_at) },
  ];

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="section-title text-xl">Audit Logs</h1>
          <p className="text-text-muted text-sm mt-0.5">Complete record of all admin actions</p>
        </div>
        <button onClick={handleExport} className="btn-secondary">↓ Export CSV</button>
      </div>

      {/* Filters */}
      <div className="card-padded mb-5 flex flex-wrap gap-4">
        <Input
          label="Filter by user"
          placeholder="Username or email…"
          value={userFilter}
          onChange={e => { setUserFilter(e.target.value); table.setPage(0); }}
          className="flex-1 min-w-[180px] max-w-xs"
        />
        <Select
          label="Action type"
          value={actionFilter}
          onChange={e => { setActionFilter(e.target.value); table.setPage(0); }}
          className="flex-1 min-w-[160px] max-w-xs"
        >
          {ACTION_TYPES.map(a => <option key={a} value={a}>{a || 'All actions'}</option>)}
        </Select>
        <Input
          label="From date"
          type="date"
          value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); table.setPage(0); }}
          className="flex-1 min-w-[160px] max-w-xs"
        />
        <Input
          label="To date"
          type="date"
          value={dateTo}
          onChange={e => { setDateTo(e.target.value); table.setPage(0); }}
          className="flex-1 min-w-[160px] max-w-xs"
        />
        <div className="flex items-end">
          <button onClick={() => { setUserFilter(''); setActionFilter(''); setDateFrom(''); setDateTo(''); table.setPage(0); }}
            className="btn-secondary">Clear</button>
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          {...table}
          onPageChange={table.setPage}
          onPerPageChange={table.setPerPage}
          actions={row => (
            <button className="btn-icon" onClick={() => setDetail(row)} title="View details">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          )}
        />
      </div>

      {/* Detail Modal */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title="Log Detail" size="md">
        {detail && (
          <div className="space-y-3 text-sm">
            <Row label="User"        value={detail.user_name} />
            <Row label="Email"       value={detail.user_email} />
            <Row label="Action"      value={<code className="text-xs bg-white/[0.06] text-accent px-2 py-0.5 rounded">{detail.action}</code>} />
            <Row label="IP"          value={<code className="text-xs font-mono">{detail.ip}</code>} />
            <Row label="Date"        value={dateFormat(detail.created_at)} />
            {detail.description && <Row label="Description" value={detail.description} />}
            {detail.payload && (
              <div>
                <p className="text-text-muted text-xs mb-1">Payload</p>
                <pre className="text-xs bg-black/30 p-3 rounded-lg overflow-auto max-h-40 text-text-muted">
                  {typeof detail.payload === 'string' ? detail.payload : JSON.stringify(detail.payload, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-text-muted w-24 flex-shrink-0">{label}</span>
      <span className="text-text-main">{value ?? '—'}</span>
    </div>
  );
}
